import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Card, Checkbox, Field, Select, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { PROJECT_STATUSES, type ProjectDetail, type ProjectStatus, projectStatusName, usePeople, useProjectForm } from './data';
import { type Priority, PRIORITIES, priorityName } from './requests-data';

interface HighPriorityConflict {
  code: 'high_priority_exists';
  message: string;
  projects: Array<{ number: number; code: string; name: string }>;
}

/** Edición del proyecto (quien lo gestiona): datos, estado, prioridad, responsable, fechas y etiquetas. */
export function ProjectEditCard({ project, onClose }: { project: ProjectDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const form = useProjectForm();
  const people = usePeople(project.organizationId);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [status, setStatus] = useState<ProjectStatus>(project.status);
  const [priority, setPriority] = useState<Priority>(project.priority ?? 'media');
  const [ownerId, setOwnerId] = useState(project.ownerId ?? '');
  const [startDate, setStartDate] = useState(project.startDate ?? '');
  const [dueDate, setDueDate] = useState(project.dueDate ?? '');
  const [labels, setLabels] = useState(project.labels.map((l) => l.id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<HighPriorityConflict | null>(null);

  async function save(confirmHighPriority = false) {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/proyectos/${project.number}`, {
        method: 'PATCH',
        json: {
          name: name.trim(),
          description: description.trim() || null,
          status,
          priority,
          ownerId: ownerId || null,
          startDate: startDate || null,
          dueDate: dueDate || null,
          labelIds: labels,
          ...(confirmHighPriority ? { confirmHighPriority: true } : {}),
        },
      });
      await qc.invalidateQueries({ queryKey: ['proyectos'] });
      onClose();
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as Partial<HighPriorityConflict> | undefined) : undefined;
      if (err instanceof ApiError && err.status === 409 && body?.code === 'high_priority_exists') setConflict(body as HighPriorityConflict);
      else setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Card title={t('pr.editar')}>
      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        <Field id="pre-nombre" label={t('pr.nombre')} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        <Textarea id="pre-descripcion" label={t('pr.descripcion_campo')} rows={3} maxLength={10_000} value={description} onChange={(e) => setDescription(e.target.value)} />
        <Select id="pre-estado" label={t('pr.estado')} value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
          {PROJECT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {projectStatusName(s)}
            </option>
          ))}
        </Select>
        <Select id="pre-prioridad" label={t('pr.prioridad')} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {priorityName(p)}
            </option>
          ))}
        </Select>
        <Select id="pre-responsable" label={t('pr.responsable')} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
          <option value="">{t('pr.sin_responsable')}</option>
          {/* El responsable actual sigue en la lista aunque ya no sea asignable. */}
          {project.ownerId && !people.data?.some((p) => p.id === project.ownerId) && <option value={project.ownerId}>{project.owner ?? project.ownerId}</option>}
          {people.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.fullName}
            </option>
          ))}
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <Field id="pre-inicio" type="date" label={t('pr.inicio')} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Field id="pre-vence" type="date" label={t('pr.vence')} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
        {!!form.data?.projectLabels.length && (
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium text-slate-700">{t('pr.etiquetas')}</legend>
            {form.data.projectLabels.map((l) => (
              <Checkbox key={l.id} label={l.name} checked={labels.includes(l.id)} onChange={(e) => setLabels(e.target.checked ? [...labels, l.id] : labels.filter((x) => x !== l.id))} />
            ))}
          </fieldset>
        )}
        {conflict ? (
          <Alert tone="warning">
            <p>{conflict.message}</p>
            <ul className="mt-1 list-disc pl-5">
              {conflict.projects.map((p) => (
                <li key={p.number}>
                  {p.code} · {p.name}
                </li>
              ))}
            </ul>
            <div className="mt-2 flex gap-2">
              <Button disabled={busy} onClick={() => void save(true)}>
                {t('sp.confirmar_alta')}
              </Button>
              <Button variant="secondary" onClick={() => setConflict(null)}>
                {t('comun.cancelar')}
              </Button>
            </div>
          </Alert>
        ) : (
          <div className="flex gap-2">
            <Button disabled={busy || name.trim().length < 3} onClick={() => void save()}>
              {t('comun.guardar')}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              {t('comun.cancelar')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
